import * as THREE from 'three';
import type { Rect } from '../../core/math';
import type { Mood } from '../../render/mood';
import type { CityLayout } from '../types';
import atlas from './skylineAtlas.json';
import { hash01 } from './meshBuilder';
// Imported up front (not on demand): the requests leave the moment the materials are made, so the files download
// while the rest of the city is built instead of after it (docs/PERF.md "Load time").
import { ktx2Loader } from '../../render/ktx2';
import { needForFirstView } from '../../render/shaderWarmup';

/**
 * The far skyline: a ring of flat backdrop cards beyond the backdrop streets (sixteen baked strips of
 * ordinary rooftops, 128 x 64 m each) and, further out, the borough's landmarks that stand above them
 * (a power station's chimney pair, gasholder frames, church spires, a tower-block cluster, dock cranes,
 * the town hall clock tower, a water tower, a brewery, a slab of flats, pylons), all silhouettes baked by
 * art/models/blender/skyline_bake.py into one atlas (public/textures/skyline.ktx2, 162 kB; layout in
 * skylineAtlas.json). One mesh, one material, one texture: one draw call and ~70 quads on every tier.
 *
 * The atlas is packed data, not colour: R the silhouette's tone, G lit windows, B the red aircraft lamps,
 * A coverage (stored sRGB-encoded, as Blender writes a PNG; the texture's sRGB decode gives the baked
 * values back). The shader colours a card from the *fog colour* scaled by the tone, so the skyline follows
 * the mood (and the mood cross-fades) for free, then hazes it with the scene's own fog density through a
 * thinner curve than the fog (`HAZE`): the real fog would swallow anything past 600 m, and a chimney
 * two kilometres off should still loom through it. Lit windows glow with the mood's window level, as the
 * facades do; the warning lamps blink. The base of every card melts into the fog, and a card seen from
 * steeply above (the overview) fades out, so the ring never reads as stage flats.
 *
 * The cards stand 200-420 m outside the playable rectangle, beyond the camera's 600 m far plane from much
 * of the district: the vertex shader clamps their depth to the far plane instead of moving the plane (which
 * would let every fully fogged chunk through frustum culling). Anything real in front still occludes them.
 */
export interface Skyline {
  readonly mesh: THREE.Mesh;
  setMood(m: Mood): void;
  update(time: number): void;
  /** Cards drawn (quads) and whether the KTX2 arrived. */
  stats(): { cards: number; loaded: boolean };
  dispose(): void;
}

export interface SkylineCard {
  /** Atlas cell name. */
  cell: string;
  /** World centre of the card's base. */
  x: number;
  z: number;
  /** Heading of the card's normal (radians; 0 faces +z, as `Math.atan2(nx, nz)`). */
  facing: number;
  mirror: boolean;
  /** Near ring (rooftop strip) or far landmark. */
  far: boolean;
}

/** Metres outside the playable rectangle: the rooftop ring and the landmark ring. */
export const NEAR_OFFSET = 200;
export const FAR_OFFSET = 420;
/** The skyline's own haze: the fog density times this, in the fog's exp2 curve. */
const HAZE = 0.45;
const M_PER_PX: number = atlas.mPerPx;
type Cell = { x: number; y: number; w: number; h: number };
const CELLS = atlas.cells as Record<string, Cell>;
const STRIPS: string[] = atlas.strips;

/** The far landmarks: side, position along that side (m), the cell, mirrored or not. */
const LANDMARKS: { side: 'n' | 's' | 'w' | 'e'; at: number; cell: string; mirror?: boolean }[] = [
  { side: 'n', at: -120, cell: 'towers' },
  { side: 'n', at: 200, cell: 'watertower' },
  { side: 'n', at: 400, cell: 'pylon', mirror: true },
  { side: 'n', at: -430, cell: 'spire' },
  { side: 's', at: -60, cell: 'townhall' },
  { side: 's', at: -330, cell: 'steeple' },
  // the power station on the estuary bank, south-east, where the backdrop streets leave the water open
  { side: 's', at: 470, cell: 'power' },
  { side: 's', at: -570, cell: 'pylon' },
  { side: 'w', at: -250, cell: 'gas' },
  { side: 'w', at: 120, cell: 'brewery' },
  { side: 'w', at: -620, cell: 'slab' },
  { side: 'w', at: 420, cell: 'spire', mirror: true },
  { side: 'e', at: 330, cell: 'cranes' },
  { side: 'e', at: -500, cell: 'towers', mirror: true },
  { side: 'e', at: -150, cell: 'watertower', mirror: true },
  { side: 'e', at: 600, cell: 'gas', mirror: true },
];

/** Where the backdrop streets leave the estuary open (landmarks.ts backdropLots): no rooftop cards there. */
function estuary(x: number, z: number): boolean {
  return x > 92 && z > 205;
}

/** The cards of a layout's skyline (pure: tested without a renderer). */
export function skylineCards(bounds: Rect): SkylineCard[] {
  const out: SkylineCard[] = [];
  const stripW = CELLS[STRIPS[0]!]!.w * M_PER_PX;
  let k = 0;
  const strip = (x: number, z: number, facing: number): void => {
    if (estuary(x, z)) return;
    const cell = STRIPS[Math.floor(hash01(k, 11) * STRIPS.length)]!;
    out.push({ cell, x, z, facing, mirror: hash01(k, 13) < 0.5, far: false });
    k++;
  };
  const N = NEAR_OFFSET;
  const nx = Math.ceil((bounds.maxX - bounds.minX + 2 * N) / stripW);
  const nz = Math.ceil((bounds.maxZ - bounds.minZ + 2 * N) / stripW);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cz = (bounds.minZ + bounds.maxZ) / 2;
  for (let i = 0; i < nx; i++) {
    const x = cx + (i - (nx - 1) / 2) * stripW;
    strip(x, bounds.minZ - N, 0); // north row faces +z (into the borough)
    strip(x, bounds.maxZ + N, Math.PI);
  }
  for (let i = 0; i < nz; i++) {
    const z = cz + (i - (nz - 1) / 2) * stripW;
    strip(bounds.minX - N, z, Math.PI / 2); // west row faces +x
    strip(bounds.maxX + N, z, -Math.PI / 2);
  }
  const F = FAR_OFFSET;
  for (const l of LANDMARKS) {
    if (!CELLS[l.cell]) continue;
    const mirror = !!l.mirror;
    if (l.side === 'n') out.push({ cell: l.cell, x: cx + l.at, z: bounds.minZ - F, facing: 0, mirror, far: true });
    else if (l.side === 's') out.push({ cell: l.cell, x: cx + l.at, z: bounds.maxZ + F, facing: Math.PI, mirror, far: true });
    else if (l.side === 'w') out.push({ cell: l.cell, x: bounds.minX - F, z: cz + l.at, facing: Math.PI / 2, mirror, far: true });
    else out.push({ cell: l.cell, x: bounds.maxX + F, z: cz + l.at, facing: -Math.PI / 2, mirror, far: true });
  }
  return out;
}

const BASE = import.meta.env?.BASE_URL ?? './';
const SODIUM = new THREE.Color(1.0, 0.62, 0.28);

function buildGeometry(cards: SkylineCard[]): THREE.BufferGeometry {
  const n = cards.length;
  const pos = new Float32Array(n * 12);
  const uv = new Float32Array(n * 8);
  const fade = new Float32Array(n * 4);
  const seed = new Float32Array(n * 4);
  const idx = new Uint16Array(n * 6);
  // the base sits a little under the ground so the fade never shows a hard bottom edge
  const BASE_Y = -1.5;
  cards.forEach((c, i) => {
    const cell = CELLS[c.cell]!;
    const w = cell.w * M_PER_PX;
    const h = cell.h * M_PER_PX;
    // the card's right-hand direction, seen from the borough: facing is the normal's heading
    const rx = Math.cos(c.facing);
    const rz = -Math.sin(c.facing);
    const corners = [
      [c.x - (rx * w) / 2, BASE_Y, c.z - (rz * w) / 2],
      [c.x + (rx * w) / 2, BASE_Y, c.z + (rz * w) / 2],
      [c.x + (rx * w) / 2, BASE_Y + h, c.z + (rz * w) / 2],
      [c.x - (rx * w) / 2, BASE_Y + h, c.z - (rz * w) / 2],
    ];
    // KTX2 is stored bottom-up: v = 1 is the top of the atlas PNG
    const u0 = cell.x / atlas.w;
    const u1 = (cell.x + cell.w) / atlas.w;
    const v1 = 1 - cell.y / atlas.h;
    const v0 = 1 - (cell.y + cell.h) / atlas.h;
    const ua = c.mirror ? u1 : u0;
    const ub = c.mirror ? u0 : u1;
    const uvs = [
      [ua, v0],
      [ub, v0],
      [ub, v1],
      [ua, v1],
    ];
    for (let j = 0; j < 4; j++) {
      pos.set(corners[j]!, i * 12 + j * 3);
      uv.set(uvs[j]!, i * 8 + j * 2);
      fade[i * 4 + j] = j < 2 ? 0 : 1;
      seed[i * 4 + j] = hash01(i, 17);
    }
    idx.set([0, 1, 2, 0, 2, 3].map((v) => v + i * 4), i * 6);
  });
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  geo.setAttribute('aFade', new THREE.BufferAttribute(fade, 1));
  geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
  geo.setIndex(new THREE.BufferAttribute(idx, 1));
  return geo;
}

const VERT = /* glsl */ `
attribute float aFade;
attribute float aSeed;
varying vec2 vUv;
varying float vFade;
varying float vSeed;
varying vec3 vWorld;
void main() {
  vUv = uv;
  vFade = aFade;
  vSeed = aSeed;
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vWorld = wp.xyz;
  vec4 cp = projectionMatrix * viewMatrix * wp;
  // the cards stand beyond the camera's far plane from most of the district: pin their depth just inside it
  cp.z = cp.w * (1.0 - 2.0e-6);
  gl_Position = cp;
}`;

const FRAG = /* glsl */ `
uniform sampler2D uMap;
uniform vec3 uFogColor;
uniform float uFogDensity;
uniform float uHaze;
uniform float uMinHaze;
uniform float uShade;
uniform vec3 uWindow;
uniform float uBeacon;
uniform float uTime;
varying vec2 vUv;
varying float vFade;
varying float vSeed;
varying vec3 vWorld;
void main() {
  vec4 t = texture2D(uMap, vUv);
  vec3 toCam = cameraPosition - vWorld;
  float d = length(toCam);
  // the base melts into the fog; a card seen from steeply above (the overview) fades away
  float a = t.a * smoothstep(0.0, 0.1, vFade) * (1.0 - smoothstep(0.3, 0.6, toCam.y / d));
  if (a < 0.01) discard;
  float f = 1.0 - exp(-pow(d * uFogDensity * uHaze, 2.0));
  f = clamp(f, uMinHaze, 0.92);
  vec3 c = uFogColor * mix(uShade * 0.72, uShade * 1.28, t.r);
  c = mix(c, uFogColor, f);
  // lights carry further through the haze than the walls do
  float pierce = 1.0 - f * 0.6;
  c += uWindow * t.g * pierce;
  float blink = step(0.55, fract(uTime * 0.6 + vSeed));
  c += vec3(1.0, 0.08, 0.03) * uBeacon * t.b * blink * pierce;
  gl_FragColor = vec4(c, a);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

export function createSkyline(scene: THREE.Scene, layout: CityLayout): Skyline {
  const cards = skylineCards(layout.bounds);
  const geo = buildGeometry(cards);
  const uniforms = {
    uMap: { value: null as THREE.Texture | null },
    uFogColor: { value: new THREE.Color(0x232a36) },
    uFogDensity: { value: 0.0026 },
    uHaze: { value: HAZE },
    uMinHaze: { value: 0.2 },
    uShade: { value: 0.65 },
    uWindow: { value: new THREE.Color(0, 0, 0) },
    uBeacon: { value: 0 },
    uTime: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    // Flat cards: three draws a transparent double-sided mesh twice (back faces, then front, each with
    // a program re-fetch); one pass draws exactly the same pixels here (docs/PERF.md "Phone pass").
    forceSinglePass: true,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'skyline';
  mesh.matrixAutoUpdate = false;
  // the ring surrounds everything: its bounding sphere would fail the far-plane test the shader sidesteps
  mesh.frustumCulled = false;
  // after the opaque world (it occludes the cards by depth), before the puddles and the river mist
  mesh.renderOrder = 1;
  mesh.visible = false;
  let loaded = false;
  let disposed = false;
  // on the horizon of the first view: the loading screen waits for it (render/shaderWarmup.ts)
  needForFirstView((async () => {
    try {
      const t = await ktx2Loader().loadAsync(`${BASE}textures/skyline.ktx2`);
      if (disposed) {
        t.dispose();
        return;
      }
      // the atlas is packed data stored sRGB-encoded (skyline_bake.py): the decode gives the baked values back
      t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
      t.anisotropy = 4;
      t.needsUpdate = true;
      uniforms.uMap.value = t;
      loaded = true;
      mesh.visible = true;
    } catch {
      // no file or no KTX2 support (tests): the horizon stays as it was
    }
  })());
  return {
    mesh,
    setMood(m) {
      // day (0.16) -> none of the glow, dusk (0.9) and night -> all of it, as the facades' window level
      const k = Math.min(1, Math.max(0, (m.windows - 0.2) / 0.6));
      // silhouettes a step darker than the bright day fog, nearly as dark as the dusk / night sky
      uniforms.uShade.value = 0.68 + (0.55 - 0.68) * k;
      uniforms.uWindow.value.copy(SODIUM).multiplyScalar(0.75 * k);
      uniforms.uBeacon.value = k;
    },
    update(time) {
      uniforms.uTime.value = time;
      const fog = scene.fog as THREE.FogExp2 | null;
      if (fog) {
        uniforms.uFogColor.value.copy(fog.color);
        uniforms.uFogDensity.value = fog.density;
      }
    },
    stats() {
      return { cards: cards.length, loaded };
    },
    dispose() {
      disposed = true;
      geo.dispose();
      mat.dispose();
      uniforms.uMap.value?.dispose();
    },
  };
}
